import { useCallback, useEffect, useRef, useState } from "react";
import {
  pickupIneligibleShipments,
  type EligibleShipment,
  type PickupIneligibleShipment,
} from "@/api/use-schedule-pickup";

/** Keep selected parcels until the customer explicitly removes unavailable ones. */
export function usePickupSelectionRecovery(
  shipments: EligibleShipment[] | undefined,
  selectedIds: Set<string>,
  onSelectionChange: (ids: Set<string>) => void
) {
  const known = useRef(new Map<string, EligibleShipment>());
  const [serverIssues, setServerIssues] = useState<PickupIneligibleShipment[]>(
    []
  );
  useEffect(() => {
    for (const shipment of shipments ?? [])
      known.current.set(shipment.id, shipment);
  }, [shipments]);
  useEffect(() => {
    setServerIssues((issues) =>
      issues.some((issue) => !selectedIds.has(issue.id))
        ? issues.filter((issue) => selectedIds.has(issue.id))
        : issues
    );
  }, [selectedIds]);

  const unavailable = [...selectedIds].flatMap((id) => {
    const serverIssue = serverIssues.find((issue) => issue.id === id);
    if (serverIssue) return [serverIssue];
    const current = shipments?.find((shipment) => shipment.id === id);
    const previous = current ?? known.current.get(id);
    if (
      current?.pickupEligibility?.eligible === false ||
      (shipments && !current)
    ) {
      return [
        {
          id,
          shipmentNumber: previous?.shipmentNumber,
          reason: current?.pickupEligibility?.reason ?? "TASK_UNAVAILABLE",
        },
      ];
    }
    return [];
  });

  const reportError = useCallback((error: unknown) => {
    const issues = pickupIneligibleShipments(error);
    if (!issues.length) return false;
    setServerIssues(issues);
    return true;
  }, []);
  const removeUnavailable = () => {
    const blocked = new Set(unavailable.map((shipment) => shipment.id));
    const remaining = new Set(
      [...selectedIds].filter((id) => !blocked.has(id))
    );
    onSelectionChange(remaining);
    setServerIssues([]);
    return remaining;
  };
  return { unavailable, reportError, removeUnavailable };
}
